import type { OtsBadgeTone } from "../components/OtsBadge";
import type { OtsSpanTone } from "../components/OtsSpanTimeline";
import type { ActivityEntry, ActivityEntryKind, ActivityMarkerTone } from "./driver-activity-view-model";

export type ActivityKindTheme = {
  label: string;
  badgeTone: OtsBadgeTone;
  /** Color bar down the row's left edge. */
  rail: string;
  /** Row background; empty for ordinary tickets. */
  tint: string;
  span: OtsSpanTone;
};

/**
 * One color per kind of row, echoing the WaterGo grid (green start shift, red end shift, purple
 * geofence, orange site work, blue tank drops) in theme tokens so it holds up in dark mode.
 */
export const activityKindTheme: Record<ActivityEntryKind, ActivityKindTheme> = {
  load: { label: "Load", badgeTone: "info", rail: "", tint: "", span: "primary" },
  pickup: { label: "Pickup only", badgeTone: "warning", rail: "shadow-[inset_4px_0_0_var(--ots-warning)]", tint: "bg-[var(--ots-warning-container)]/70", span: "warning" },
  delivery: { label: "Delivery only", badgeTone: "warning", rail: "shadow-[inset_4px_0_0_var(--ots-warning)]", tint: "bg-[var(--ots-warning-container)]/70", span: "warning" },
  visit: { label: "Site visit", badgeTone: "violet", rail: "shadow-[inset_4px_0_0_var(--ots-violet)]", tint: "bg-[var(--ots-violet-container)] text-[var(--ots-on-violet-container)]", span: "violet" },
  dispatch: { label: "Dispatch", badgeTone: "indigo", rail: "shadow-[inset_4px_0_0_var(--ots-indigo)]", tint: "bg-[var(--ots-indigo-container)] text-[var(--ots-on-indigo-container)]", span: "neutral" },
  siteWork: { label: "Site work", badgeTone: "yellow", rail: "shadow-[inset_4px_0_0_var(--ots-yellow)]", tint: "bg-[var(--ots-yellow-container)] text-[var(--ots-on-yellow-container)]", span: "yellow" },
  tankDrop: { label: "Tank drop", badgeTone: "blue", rail: "shadow-[inset_4px_0_0_var(--ots-blue)]", tint: "bg-[var(--ots-blue-container)] text-[var(--ots-on-blue-container)]", span: "blue" },
};

/** Service-work tickets take the site-work color, as in WaterGo; other tickets have no tint of their own. */
export const activityTintClass = (entry: ActivityEntry): string =>
  entry.isServiceTicket ? activityKindTheme.siteWork.tint : activityKindTheme[entry.kind].tint;

/** Loads carry their commodity's color instead of a kind color. */
export const activityRailClass = (entry: ActivityEntry): string => {
  if (entry.isServiceTicket) return activityKindTheme.siteWork.rail;
  if (entry.kind !== "load") return activityKindTheme[entry.kind].rail;
  const category = entry.category?.toUpperCase();
  return category === "OIL" ? "shadow-[inset_4px_0_0_var(--ots-transport-category-oil)]"
    : category === "WATER" ? "shadow-[inset_4px_0_0_var(--ots-transport-category-water)]"
      : "";
};

/** Timeline color: trouble wins over kind. */
export const activitySpanTone = (entry: ActivityEntry): OtsSpanTone =>
  entry.warnings.length ? "danger" : entry.isServiceTicket ? activityKindTheme.siteWork.span : activityKindTheme[entry.kind].span;

export const activityMarkerToneClasses: Record<ActivityMarkerTone, string> = {
  neutral: "text-[var(--ots-text-muted)]",
  info: "text-[var(--ots-primary)]",
  success: "text-[var(--ots-success)]",
  warning: "text-[var(--ots-warning)]",
  danger: "text-[var(--ots-error)]",
  violet: "text-[var(--ots-violet)]",
};
